import React, { useCallback, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Checkbox, FormControlLabel, Paper, TextField, Typography } from '@mui/material';

import {
  VALID_DATE,
  NOT_VALID_DATE,
  VALID_TIME,
  INVALID_TIME,
  IN_PAST_DATE,
} from '../../../../shared/constants';

import {
  combineFullDateFromInputs,
  getInputFieldValuesFromDate,
  validateMonthRange,
} from '../SelectDocumentCreationDate/dateUtils';

import { BackButton, PageTitle } from '../../../../components/common';
import ButtonContainer from '../../../../components/ButtonContainer';
import { dateDiff } from '../../../../shared';
import DatePicker from '../common/DatePicker';

import { StyledVerticalContent } from './DateAndTime.styled';

function DateAndTime(props) {
  const {
    backButtonLink,
    nextButtonLink,
    pageTitleId,
    dateLabelId,
    timeLabelId,
    selectedDate,
    selectedTime,
    timeNotAvailable,
    onDateChange,
    onTimeChange,
    onTimeNotAvailable,
  } = props;

  const history = useHistory();
  const intl = useIntl();

  const dateInputFieldValues = getInputFieldValuesFromDate(selectedDate);
  const [date, setDate] = useState(dateInputFieldValues);

  const [liveValidation, setLiveValidation] = useState(false);

  const { day, month, year } = date;
  const setDay = (newDay) => setDate({ day: newDay, month, year });
  const setMonth = (newMonth) => setDate({ day, month: newMonth, year });
  const setYear = (newYear) => setDate({ day, month, year: newYear });

  const [dateStatus, setDateStatus] = useState(VALID_DATE);
  const [timeStatus, setTimeStatus] = useState(VALID_TIME);

  useEffect(() => {
    return () => {
      setLiveValidation(false);
    };
  }, []);

  useEffect(() => {
    const newDate = combineFullDateFromInputs(day, month, year);
    const newDateObject = new Date(newDate);
    const currentDate = new Date();
    const isValidDate = !Number.isNaN(Number(newDateObject));
    if (isValidDate) {
      setDateStatus(VALID_DATE);
    }

    const notInTheFuture = isValidDate ? newDateObject < currentDate : false;
    if (notInTheFuture && validateMonthRange(newDate)) {
      onDateChange(newDate);
    } else {
      onDateChange('');
    }
  }, [day, month, year, onDateChange]);

  const validateDate = useCallback(() => {
    if (!selectedDate || !validateMonthRange(selectedDate)) {
      setDateStatus(NOT_VALID_DATE);
      return false;
    }

    if (
      selectedDate &&
      validateMonthRange(selectedDate) &&
      dateDiff('year', new Date(selectedDate)) >= 5
    ) {
      setDateStatus(IN_PAST_DATE);
      return false;
    }

    return true;
  }, [selectedDate]);

  const validateTime = useCallback(() => {
    if (timeNotAvailable) return true;

    const [hour, minute] = selectedTime?.split(':') || [];

    const hourInNumber = Number(hour);
    const minuteInNumber = Number(minute);

    const validHour = !Number.isNaN(hourInNumber) && hourInNumber >= 0 && hourInNumber <= 23;
    const validMinute =
      !Number.isNaN(minuteInNumber) && minuteInNumber >= 0 && minuteInNumber <= 59;

    if (validHour && validMinute) {
      setTimeStatus(VALID_TIME);
      return true;
    }

    setTimeStatus(INVALID_TIME);
    return false;
  }, [selectedTime, timeNotAvailable]);

  useEffect(() => {
    if (!selectedTime || !liveValidation || !selectedDate) return;

    validateTime();
    validateDate();
  }, [liveValidation, selectedTime, selectedDate, validateTime, validateDate]);

  const handleButtonClick = () => {
    setLiveValidation(true);
    if (!validateDate() || !validateTime()) return;

    history.push(nextButtonLink);
  };

  const handleBlurTimeInput = () => {
    const [hour, minute] = selectedTime?.split(':') || [];

    if (hour && !minute) {
      onTimeChange(`${hour}:00`);
    }
  };

  const handleCheckboxChange = (event) => {
    const checked = event?.target.checked;

    if (checked) {
      onTimeChange('');
      setTimeStatus(VALID_TIME);
    }

    onTimeNotAvailable(checked);
  };

  const validDate = dateStatus === VALID_DATE && !!day && !!month && !!year && year.length === 4;

  const validTime = !!timeNotAvailable || (!!selectedTime && timeStatus === VALID_TIME);

  const validForm = validDate && validTime;

  return (
    <>
      <BackButton to={backButtonLink}>
        <Typography variant="h3">
          <FormattedMessage id="common.button.back" />
        </Typography>
      </BackButton>

      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage id={pageTitleId} />
        </Typography>
      </PageTitle>

      <StyledVerticalContent>
        <Paper>
          <StyledVerticalContent>
            <DatePicker
              label={intl.formatMessage({ id: dateLabelId })}
              day={day}
              month={month}
              year={year}
              setDay={setDay}
              setMonth={setMonth}
              setYear={setYear}
            />

            {dateStatus === NOT_VALID_DATE && (
              <Typography variant="p" color="error" data-testid="error-date">
                <FormattedMessage id="new_claim.invalid.date" />
              </Typography>
            )}

            {dateStatus === IN_PAST_DATE && (
              <Typography variant="p" color="error" data-testid="error-date">
                <FormattedMessage id="new_claim.invalid.date_past" />
              </Typography>
            )}

            <TextField
              fullWidth
              id="time-input"
              name="time-input"
              placeholder="HH:MM"
              label={
                <Typography variant="h3" component="span">
                  <FormattedMessage id={timeLabelId} />
                </Typography>
              }
              value={selectedTime || ''}
              onChange={(e) => onTimeChange(e?.target?.value)}
              onBlur={handleBlurTimeInput}
              disabled={timeNotAvailable}
              error={timeStatus === INVALID_TIME}
              helperText={
                timeStatus === INVALID_TIME && (
                  <Typography variant="p" color="error" data-testid="error-time">
                    <FormattedMessage id="common.form.error.invalid_time" />
                  </Typography>
                )
              }
            />
          </StyledVerticalContent>
        </Paper>

        <FormControlLabel
          control={
            <Checkbox
              id="time-not-available-checkbox"
              name="time-not-available-checkbox"
              checked={timeNotAvailable}
              onChange={handleCheckboxChange}
              slotProps={{
                input: {
                  'aria-label': intl.formatMessage({
                    id: 'pet_liability.new_claim.incident_date_time.checkbox.label.dont_know_time',
                  }),
                  'aria-checked': timeNotAvailable ? 'true' : 'false',
                },
              }}
            />
          }
          label={
            <Typography variant="p">
              <FormattedMessage id="pet_liability.new_claim.incident_date_time.checkbox.label.dont_know_time" />
            </Typography>
          }
        />
      </StyledVerticalContent>

      <ButtonContainer>
        <Button data-testid="submit-date-time" onClick={handleButtonClick} disabled={!validForm}>
          <Typography variant="buttons">
            <FormattedMessage id="common.button.next" />
          </Typography>
        </Button>
      </ButtonContainer>
    </>
  );
}

export default DateAndTime;

DateAndTime.defaultProps = {
  dateLabelId: 'new_claim.form.label.date',
  timeLabelId: 'new_claim.form.label.time',
};

DateAndTime.propTypes = {
  backButtonLink: PropTypes.string,
  nextButtonLink: PropTypes.string,
  pageTitleId: PropTypes.string,
  dateLabelId: PropTypes.string,
  timeLabelId: PropTypes.string,
  selectedDate: PropTypes.string,
  selectedTime: PropTypes.string,
  onDateChange: PropTypes.func,
  onTimeChange: PropTypes.func,
};
